/* eslint-disable no-underscore-dangle */

/**
 * @function init creates and returns an instance of a class for interacting with the database
 * @param {object} firebase 
 */
function init( firebase ) {
  /**
   * @class Returns a new FirebaseStore class that will use the firebase library passed in. This should be either 'firebase' (on browser) or 'firebase-admin' on server.
   * @param {object} ref object with methods to interact with the database
   **/
  class Access {

    constructor( ref ) {
      this._ref = ref
    }

    /**
     * @function _refFor returns the reference for the given path
     * @param {string} path the path to return the ref of
     */
    _refFor( path ) {
      return path == null || path === '' ? this._ref : this._ref.child( path )
    }

    /**
     * @function child returns an access object for the provided path
     * @param {string} path the path to return the new access object for
     */
    child( path ) {
      return new Access( this._refFor( path ) )
    }

    /**
     * @function on starts a listener on the provided path
     * @param {string} path the path to start the listener on 
     * @param {function} onComplete 
     * @param {function} onCancelled 
     * @param {object} cxt 
     */
    on( path, onComplete, onCancelled, cxt ) {
      return this._refFor( path ).on( 'value', onComplete, onCancelled, cxt )
    }

    /**
     * @function off starts a listener on the provided path
     * @param {string} path the path to start the listener
     * @param {function} callback 
     * @param {object} cxt 
     */
    off( path, callback, cxt ) {
      return this._refFor( path ).off( 'value', callback, cxt )
    }

    /**
     * @function onAdd starts a listener on the provided path for children being added
     * @param {string} path the path to listen on
     * @param {function} onComplete 
     * @param {function} onCancelled 
     * @param {object} cxt 
     */
    onAdd( path, onComplete, onCancelled, cxt ) {
      return this._refFor( path ).on( 'child_added', onComplete, onCancelled, cxt )
    }

    /**
     * @function offAdd starts a listener on the path for children being added 
     * @param {string} path the path for the listener
     * @param {function} callback 
     * @param {object} cxt 
     */
    offAdd( path, callback, cxt ) {
      return this._refFor( path ).off( 'child_added', callback, cxt )
    }

    /**
     * @function onRemove starts a listener for children being removed
     * @param {string} path the path for the listener
     * @param {function} onComplete 
     * @param {function} onCancelled 
     * @param {object} cxt 
     */
    onRemove( path, onComplete, onCancelled, cxt ) {
      return this._refFor( path ).on( 'child_removed', onComplete, onCancelled, cxt )
    }

    /**
     * @function offRemove starts a listener for children being removed
     * @param {string} path the path for the listener
     * @param {function} callback 
     * @param {object} cxt 
     */
    offRemove( path, callback, cxt ) {
      return this._refFor( path ).off( 'child_removed', callback, cxt )
    }

    /**
   * @function onChange start a listener for when a child is changed
   * @param {string} path the path to listen on
   * @param {function} onComplete 
   * @param {function} onCancelled 
   * @param {object} cxt 
   */
    onChange( path, onComplete, onCancelled, cxt ) {
      return this._refFor( path ).on( 'child_changed', onComplete, onCancelled, cxt )
    }

    /**
   * @function offChange start a listener for when a child is changed
   * @param {string} path the path to listen on
   * @param {function} callback 
   * @param {object} cxt 
   */
    offChange( path, callback, cxt ) {
      return this._refFor( path ).off( 'child_changed', callback, cxt )
    }

    /**
     * @function once makes a one time call to the database for information
     * @param {string} path the path to get
     * @param {function} onComplete 
     * @param {function} onError 
     */
    once( path, onComplete, onError ) {
      return this._refFor( path ).once( 'value', onComplete, onError )
    }

    /**
     * @function set sets the value at the path
     * @param {string} path the path to set
     * @param {any} value value that will be set
     * @param {function} onComplete 
     */
    set( path, value, onComplete ) {
      return this._refFor( path ).set( value, onComplete )
    }

    /**
     * @function update updates the value at the path
     * @param {string} path the path to update
     * @param {any} value value that will be updated
     * @param {function} onComplete 
     */
    update( path, value, onComplete ) {
      return this._refFor( path ).update( value, onComplete )
    }

    /** 
     * @function remove removes the value at the path
     * @param {string} path the path of the data to be removed
     * @param {function} onComplete 
     */
    remove( path, onComplete ) {
      return this._refFor( path ).remove( onComplete )
    }

    /**
     * @function timestampValue returns the timestamp of the firebase server
     */
    timestampValue() {
      return firebase.database.ServerValue.TIMESTAMP
    }
  } 

  /**
   * @class contains many methods for interacting with the firebase database
   * @param {object} options firebase config
   */
  class FirebaseStore {
    
    constructor( options ) {

      this.options = options
      let opts = {}
      if ( options.firebaseApp && options.rootRef ) {
        this._app = options.firebaseApp
        this._rootRef = options.rootRef
      } else {
        let name
        if ( options.config ) {
          opts = options.config
          name = `${options.config.databaseURL}/${options.root}`
        } else if ( options.databaseURL ) {
          opts = {
            databaseURL : options.databaseURL,
            credential : firebase.credential.applicationDefault()
          }
          name = options.databaseURL
        } else {
          opts = {}
        }

        // find the app with the same root name
        const app = appByName( name )

        // this will fail on the second call without a name
        this._app = app || firebase.initializeApp( opts, name )
        this._rootRef = this._app.database().ref()
      }

      this._jwt = options.jwt

      let root = options.root || ''
      if ( root !== '' && !root.endsWith( '/' ) ) {
        root += '/'
      }
      this.root = root

      this._dataRef = this._rootRef.child( `${root}data` )
      this._metaRef = this._rootRef.child( `${root}meta` )
      this._srvRef = this._rootRef.child( `${root}srv` )
      this._deviceRef = this._rootRef.child( `${root}device` )
    }

    /**
     * @function _refFor gets a database reference for the path
     * @param {string} path path to get a database reference for
     */
    _refFor( path ) {
      return path == null || path === '' ? this._dataRef : this._dataRef.child( path )
    }

    /**
     * @function _metaRefFor gets a database reference for the meta data of the path 
     * @param {string} path path to get the meta for
     */
    _metaRefFor( path ) {
      return path == null || path === '' ? this._metaRef : this._metaRef.child( path )
    }

    /**
     * @function _serviceRefFor gets a database reference for the service data of the path
     * @param {string} path path to get the service for
     */
    _serviceRefFor( path ) {
      return path == null || path === '' ? this._srvRef : this._srvRef.child( path )
    }

    /**
     * @function signIn If on the client side, sign in with a custom jwt token
     * @param {string} jwt webtoken to use for authentication
     **/
    signIn( jwt ) {
      return this._app.auth().signInWithCustomToken( jwt || this._jwt )
    }

    /**
     * @function child returns a new FirebaseStore with the a new root
     * @param {string} path appended to the current root to make the root of the new FirebaseStore
     */
    child( path ) {
      return new FirebaseStore( {
        firebaseApp : this._app,
        rootRef : this._rootRef,
        jwt : this.options.jwt,
        root : this.root + path
      } )
    }

    /**
     * @function root Returns an Access object to the root. Not normally used
     **/
    root( ) {
      return new Access( this._rootRef )
    }

    /**
     * @function data returns a new Access object to the data root
     */
    data() {
      if ( this._dataAccess == null ) {
        this._dataAccess = new Access( this._dataRef )
      }
      return this._dataAccess
    }

    /**
     * @function meta returns a new Access object to the meta root
     */
    meta() {
      if ( this._metaAccess == null ) {
        this._metaAccess = new Access( this._metaRef )
      }
      return this._metaAccess
    }

    /**
     * @function device returns a new Accees object to the device root
     */
    device() {
      if ( this._deviceAccess == null ) {
        this._deviceAccess = new Access( this._deviceRef )
      }
      return this._deviceAccess
    }

    /**
     * @function access Creates an Access object referencing the location reative to the current root
     * @param {string} path path for a new access object
     **/
    access( path ) { 
      return new Access( path == null || path === '' ? this._rootRef : this._rootRef.child( path ) )
    }

    /**
     * @function service creates an Access object referencing the service data. If invoked with no arguments, this will return a Access object pointing at root/srv. Not normal use (monitoring perhaps)
     * @param {string} name name for the service reference
     */ 
    service( name ) {
      if ( arguments.length === 0 ) {
        return new Access( this._srvRef )
      }
      name = name == null ? null : name.trim()
      if ( name == null || name.length === 0 ) {
        console.log( 'invalid name' ) /* eslint-disable-line no-console */
        throw new Error( 'service must be invoked with a non-null name' )
      }
      return new Access( this._serviceRefFor( name ) )
    }

    // Data Access Methods

    /**
     * @function on creates a listener for when the data at the path is changed
     * @param {string} path path to the desired data to be listened to
     * @param {function} onComplete 
     * @param {function} onCancelled 
     * @param {object} cxt 
     */
    on( path, onComplete, onCancelled, cxt ) {
      return this.data().on( path, onComplete, onCancelled, cxt )
    }

    /**
     * @function off create a listener for when the data at the path is changed
     * @param {string} path path to the desired data to be listened to
     * @param {function} callback 
     * @param {object} cxt 
     */
    off( path, callback, cxt ) {
      return this.data().off( path, callback, cxt )
    }

    /**
     * @function once get the data one time
     * @param {string} path path to get the data from 
     * @param {function} onComplete 
     * @param {function} onError 
     */
    once( path, onComplete, onError ) {
      return this.data().once( path, onComplete, onError )
    }

    /**
     * @function set sets the data at the path
     * @param {string} path path to set the value at
     * @param {any} value value to set
     * @param {function} onComplete 
     */
    set( path, value, onComplete ) {
      return this.data().set( path, value, onComplete )
    }

    /**
     * @function update updates the data at the path
     * @param {string} path path to update the value of 
     * @param {any} value value to update
     * @param {function} onComplete 
     */
    update( path, value, onComplete ) {
      return this.data().update( path, value, onComplete )
    }

    /**
     * @function remove removes the data at the path
     * @param {string} path path to remove
     * @param {function} onComplete 
     */
    remove( path, onComplete ) {
      return this.data().remove( path, onComplete )
    }

    // Meta Access Methods

    /**
     * @function metaOn adds a listener to the meta data at the path
     * @param {string} path the path to listen to the meta on
     * @param {function} onComplete 
     * @param {function} onCancelled 
     * @param {object} cxt 
     */
    metaOn( path, onComplete, onCancelled, cxt ) {
      return this.meta().on( path, onComplete, onCancelled, cxt )
    }

    /**
     * @function metaOff adds a listener to the meta data at the path
     * @param {string} path the path to listen to the meta
     * @param {function} callback 
     * @param {object} cxt 
     */
    metaOff( path, callback, cxt ) {
      return this.meta().off( path, callback, cxt )
    }

    /**
     * @function metaOnce gets the meta data once
     * @param {string} path the path to get the meta from 
     * @param {function} onComplete 
     * @param {function} onError 
     */
    metaOnce( path, onComplete, onError ) {
      return this.meta().once( path, onComplete, onError )
    }

    /**
     * @function metaSet sets the meta data
     * @param {string} path the path to set the meta on
     * @param {any} value the data to set at the path
     * @param {function} onComplete 
     */
    metaSet( path, value, onComplete ) {
      return this.meta().set( path, value, onComplete )
    }

    /**
     * @function metaUpdate updates the meta data
     * @param {string} path the path to update the meta on
     * @param {any} value the data to update the meta with
     * @param {function} onComplete 
     */
    metaUpdate( path, value, onComplete ) {
      return this.meta().update( path, value, onComplete )
    }

    /**
     * @function metaRemove removes the meta at the path
     * @param {string} path path to remove the meta on
     * @param {function} onComplete 
     */
    metaRemove( path, onComplete ) {
      return this.meta().remove( path, onComplete )
    }
    
    /**
     * @function timestampValue gets the timestamp of the database
     */
    timestampValue() {
      return firebase.database.ServerValue.TIMESTAMP
    }
  }

  /**
   * @function appByName gets a firebase app based off its name
   * @param {string} name firebase app to use
   */
  function appByName( name ) {
    for ( let n = 0; n < firebase.apps.length || 0; n++ ) {
      if ( firebase.apps[n].name === name ) {
        return firebase.apps[n]
      }
    }
    return null
  }

  return FirebaseStore
}

export default init
